import { useSyncExternalStore } from 'react';

/**
 * A short list of ids kept in this browser's localStorage, readable from React.
 *
 * What a guest's wishlist and the "recently viewed" trail are: per-browser
 * conveniences that need no account. Every read and write is wrapped, so
 * private browsing or blocked storage degrades to a list that lives only as
 * long as the page does rather than to a crash.
 *
 * Keyed by the data source, the way the cart token is: a merchant walking
 * through theme previews must not see a preview's hearts on their own shop.
 */
export interface LocalList {
  get(): readonly string[];
  set(ids: readonly string[]): void;
  subscribe(listener: () => void): () => void;
}

const EMPTY: readonly string[] = Object.freeze([]);
const lists = new Map<string, LocalList>();

function read(key: string): readonly string[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return EMPTY;
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return EMPTY;
    return parsed.filter((value): value is string => typeof value === 'string' && value.length > 0);
  } catch {
    return EMPTY;
  }
}

/** The list stored under `name` for one data source, at most `max` long. */
export function localList(name: string, sourceKey: string, max: number): LocalList {
  const key = sourceKey === 'live' ? name : `${name}:${sourceKey}`;
  const existing = lists.get(key);
  if (existing) return existing;

  let cache: readonly string[] | null = null;
  const listeners = new Set<() => void>();
  const notify = () => listeners.forEach((listener) => listener());

  const list: LocalList = {
    get() {
      if (cache === null) cache = read(key);
      return cache;
    },
    set(ids) {
      const next = Object.freeze([...new Set(ids)].slice(0, max));
      cache = next.length === 0 ? EMPTY : next;
      try {
        if (next.length === 0) localStorage.removeItem(key);
        else localStorage.setItem(key, JSON.stringify(next));
      } catch {
        /* kept for this page only */
      }
      notify();
    },
    subscribe(listener) {
      listeners.add(listener);
      // Another tab changed it: re-read on next get.
      const onStorage = (event: StorageEvent) => {
        if (event.key !== null && event.key !== key) return;
        cache = null;
        listener();
      };
      window.addEventListener('storage', onStorage);
      return () => {
        listeners.delete(listener);
        window.removeEventListener('storage', onStorage);
      };
    },
  };
  lists.set(key, list);
  return list;
}

/** Drop every cached list, so a test's cleared localStorage is read afresh. */
export function resetLocalLists(): void {
  lists.clear();
}

export function useLocalList(list: LocalList): readonly string[] {
  return useSyncExternalStore(list.subscribe, list.get, list.get);
}
